/**
 * Where the game pays out — the impure glue between play and the wallet.
 *
 * rewards.ts decides AMOUNTS and is pure; this file decides WHEN, touches
 * storage, and owns the idempotency keys. Keeping the split means the reward
 * table stays testable without a browser and the awkward part — "has this
 * already been paid?" — lives in exactly one place.
 *
 * ── Every payout is keyed, and the keys are chosen to be stable ─────────────
 *
 * A season is `season:<club>:<n>` and a knockout round is `wc:<seed>:r<n>`,
 * because both are awarded from SCREEN code that re-renders freely — the same
 * hazard that already produced a trophy-per-repaint bug twice in this project.
 * A match is keyed on its finish time, which is safe for the opposite reason:
 * it is awarded from a one-shot callback that fires exactly once per match, and
 * a stable key there would wrongly suppress the second match of a session.
 *
 * The rule: derive the key from the THING, not from the moment, wherever the
 * thing has an identity.
 */

import { credit, loadWallet } from './wallet.js';
import { knockoutReward, matchReward, seasonReward, type MatchReward } from './rewards.js';

export interface Payout {
  readonly reward: MatchReward;
  /** Coins actually added. 0 when the payout had already been made. */
  readonly credited: number;
  readonly balance: number;
}

const EMPTY: MatchReward = { total: 0, lines: [] };

/**
 * Pay for a finished match. `scored`/`conceded` are the PLAYER's scoreline.
 *
 * Abandoned matches pay nothing — `finished` is the same flag the competition
 * store uses to decide whether a scoreline counts, and paying for a quit at 0-0
 * in the eighth minute would make quitting the most efficient way to earn.
 */
export function awardMatch(scored: number, conceded: number, finished: boolean): Payout {
  if (!finished) return { reward: EMPTY, credited: 0, balance: loadWallet().balance };

  const reward = matchReward(scored, conceded);
  const credited = credit(`match:${Date.now()}`, reward.total, 'match', 'Match reward');
  return { reward, credited, balance: loadWallet().balance };
}

/** Pay for a completed league season. Keyed on the season itself. */
export function awardSeason(clubId: string, season: number, position: number): Payout {
  const reward = seasonReward(position);
  const credited = credit(
    `season:${clubId}:${season}`,
    reward.total,
    'season',
    `Season ${season} — ${position === 1 ? 'champions' : `${position}th`}`,
  );
  return { reward, credited, balance: loadWallet().balance };
}

/**
 * Pay for reaching a knockout round. Awarded on ENTERING the round, so a run
 * that ends in the quarter-finals is still paid for getting there — the same
 * principle as paying for a defeat.
 */
export function awardKnockout(
  seed: number,
  round: number,
  wonTournament: boolean,
): Payout {
  const total = knockoutReward(round, wonTournament);
  if (total <= 0) return { reward: EMPTY, credited: 0, balance: loadWallet().balance };
  const label = wonTournament ? 'World Cup won' : `Reached knockout round ${round + 1}`;
  const reward: MatchReward = { total, lines: [{ label, amount: total }] };
  const credited = credit(
    `wc:${seed}:r${round}${wonTournament ? ':trophy' : ''}`,
    total,
    'tournament',
    label,
  );
  return { reward, credited, balance: loadWallet().balance };
}
